import { useMemo } from 'react'

import { formatMoney } from 'accounting'
import { DateTime } from 'luxon'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSignOut } from '@fortawesome/pro-light-svg-icons'
import { faStar } from '@fortawesome/pro-solid-svg-icons'

import {
  Link, StatusBadge, Table, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { buildFullDescription, buildOrderItemUrl } from '@functions/orderItem'

import useSelector from '@hooks/useSelector'

import type { OrderItemModel } from '@models/types'

import { statusColors } from '@functions/status'

import type { EntitiesState } from '@redux/entities'

type BuildColumnsParams = {
  campaigns: EntitiesState['campaigns'],
  orders: EntitiesState['orders'],
  publications: EntitiesState['publications'],
  publicationEditions: EntitiesState['publicationEditions'],
  selectedPriorityUserId?: number,
  statuses: EntitiesState['statuses'],
}
const buildColumns = (
  params: BuildColumnsParams,
) => {
  const {
    campaigns, orders, publications, publicationEditions, selectedPriorityUserId, statuses,
  } = params

  return [
    {
      title: '',
      dataKey: 'cache_key',
      cellProps: {
        verticalAlign: 'middle',
        width: 12,
      },
      render: (cache_key: number, orderItem: OrderItemModel) => (
        selectedPriorityUserId && orderItem?.priority_campaign_user_ids?.includes(selectedPriorityUserId) && (
        <Text color="blue">
          <FontAwesomeIcon icon={faStar} />
        </Text>
        )
      ),
    },
    {
      title: 'Order Item',
      dataKey: 'publication_id',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (publication_id: number, orderItem: OrderItemModel) => {
        const publication = publications[publication_id] || {}
        // @ts-expect-error TS2538
        const publicationEdition = publicationEditions[orderItem.publication_edition_id] || {}
        const itemTitle = buildFullDescription({ orderItem, publication, publicationEdition })

        const order = orders[orderItem.order_id] || {}
        const status = statuses[orderItem.status_id] || {}
        const url = buildOrderItemUrl({ order, orderItem, status })

        return (
          <Link
            href={url}
            textProps={{
              color: 'bodyFontColor',
              display: 'inline-block',
              variant: 'ellipsis',
            }}
          >
            {itemTitle}
          </Link>
        )
      },
    },
    {
      title: 'Campaign',
      dataKey: 'order_id',
      render: (order_id: number) => {
        const order = orders[order_id] || {}
        return (
          <Text variant="ellipsis">
            {campaigns[order.campaign_id]?.title || 'No Campaign'}
          </Text>
        )
      },
    },
    {
      title: 'Price',
      dataKey: 'price',
      render: (price: number) => formatMoney(price),
    },
    {
      title: 'Due Date',
      dataKey: 'dates',
      cellProps: {
        textAlign: 'center',
        width: 50,
      },
      render: (dates: object) => {
        const dueDate = digObject(dates, 'due.date')
        const dueDateFormatted = dueDate ? DateTime.fromFormat(dueDate, 'dd/MM/yyyy').toFormat('d LLL yyyy') : 'N/A'

        return (
          <StatusBadge
            text={dueDateFormatted}
            color="lightestGrey"
            textProps={{ color: 'bodyFontLightColor' }}
          />
        )
      },
    },
    {
      title: 'Status',
      dataKey: 'status_id',
      cellProps: {
        textAlign: 'center',
      },
      render: (status_id: number) => {
        const status = statuses[status_id] || {}

        return (
          status?.key && (
          <StatusBadge
            // @ts-expect-error TS7053
            color={statusColors[status.key]}
            ghost
            text={status.title}
          />
          )
        )
      },
    },
    {
      title: '',
      dataKey: 'id',
      cellProps: {
        width: 15,
      },
      render: (_: unknown, orderItem: OrderItemModel) => {
        const order = orders[orderItem.order_id] || {}
        const status = statuses[orderItem.status_id] || {}
        const url = buildOrderItemUrl({ order, orderItem, status })

        return (
          <Link greyLink href={url}>
            <FontAwesomeIcon icon={faSignOut} />
          </Link>
        )
      },
    },
  ]
}

const OrderItemsTable = (props: { orderItems: OrderItemModel[], selectedPriorityUserId?: number }) => {
  const { orderItems, selectedPriorityUserId } = props

  const {
    campaigns,
    orders,
    publications,
    publicationEditions,
    statuses,
    _updates: {
      // @ts-expect-error TS2339
      campaigns: campaignsUpdatedAt,
      // @ts-expect-error TS2339
      orders: ordersUpdatedAt,
      // @ts-expect-error TS2339
      statuses: statusesUpdatedAt,
    },
  } = useSelector(reduxState => reduxState.entities)

  const memoColumns = useMemo(
    () => buildColumns({
      campaigns, orders, publications, publicationEditions, selectedPriorityUserId, statuses,
    }),
    [orderItems.length, campaignsUpdatedAt, ordersUpdatedAt, selectedPriorityUserId, statusesUpdatedAt],
  )

  return (
    <Table
      boxProps={{ border: 'none', maxHeight: 800, overflowY: 'auto' }}
      // @ts-expect-error TS2322
      columns={memoColumns}
      // @ts-expect-error TS2322
      data={orderItems}
      stickyHeader
    />
  )
}

export default OrderItemsTable
